import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { selectUserValue } from "../../../../selectors";
import { isRegistered } from "../../../../config/utils";
import RoundedRectangle from "../../../components/RoundedRectangle";
import SegmentedTabs from "../../../components/SegmentedTabs";
import { useReCaptcha } from "../../../../hooks/useReCaptcha";
import { RECAPTCHA_ACTION_LOGIN } from "../../../../config/constants";
import client from "../../../../client";
import { ClavaContext } from "../../../../config/contexts";
import { translate } from "../../../../config/translator";

const LoginPending: React.FC = () => {
    const navigate = useNavigate();
    const { identifier, redirectAfter } = useParams();

    const user = useSelector(selectUserValue);
    const { l } = React.useContext(ClavaContext);
    const [secondsLeft, setSecondsLeft] = useState<number>(60);
    const { reCaptchaToken, refresh } = useReCaptcha(RECAPTCHA_ACTION_LOGIN);
    const canResend = useMemo(() => secondsLeft <= 0, [secondsLeft]);

    useEffect(() => {
        if (isRegistered(user)) {
            navigate(redirectAfter ? `/${redirectAfter}` : "/home", { replace: true });
        }
    }, [user, navigate, redirectAfter]);

    useEffect(() => {
        const t = window.setInterval(() => setSecondsLeft((s) => s - 1), 1000);
        return () => window.clearInterval(t);
    }, []);

    const onResend = () => {
        if (!reCaptchaToken || !identifier) {
            refresh();
            return;
        }
        const id = decodeURIComponent(identifier);
        const isEmail = /@/.test(id);
        const payload = isEmail ? { email: id.toLowerCase() } : { tel: id };
        client().loginWithSMS(payload as { email: string } | { tel: string }, reCaptchaToken).then(() => {
            setSecondsLeft(60);
        });
    };

    return (
        <div className="w-full px-6 xl:px-4 mx-auto xl:max-w-screen-xl">
            <div className="w-full flex flex-col items-start justify-start pt-2 pb-6 -mt-6 xl:-mt-8">
                <div className="max-w-xl w-full">
                    <RoundedRectangle hasBorder hasTitle titleType="upLeft" titleKey="loginPendingTitle" className="bg-bg-secondary dark:bg-bg-secondary-dark border-light-gray dark:border-light-gray-dark shadow-card dark:shadow-card-dark">
                        <div className="p-6">
                            <p className="text-font-600 mb-4">{translate("loginPendingDesc", l)}</p>
                            <div className="mt-2 w-full flex justify-end">
                                <SegmentedTabs
                                    className="p-0 gap-2"
                                    items={[{
                                        key: "resend",
                                        label: canResend ? translate("resendCode", l) : `${translate("resendAvailableIn", l)} ${Math.max(0, secondsLeft)}s`,
                                        onClick: onResend,
                                        disabled: !canResend,
                                    }]}
                                    activeKey={canResend ? "resend" : undefined}
                                />
                            </div>
                        </div>
                    </RoundedRectangle>
                </div>
            </div>
        </div>
    );
};

export default LoginPending;


